import { useMemo, useState } from "react";
import {
    AlertCircle,
    Clock,
    DollarSign,
    Layers,
    Radio,
    Send,
    TrendingUp,
    Users,
    Zap,
} from "lucide-react";
import { useEsteiraList } from "../api/queries";
import { useDashboardStore } from "../store/dashboardStore";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { SectionCard } from "../components/SectionCard";
import { KpiCard } from "../components/KpiCard";
import { BadgeHi } from "../components/BadgeHi";
import { DashSkeleton, SkelText } from "../components/Skeleton";
import { ApiBanner } from "../components/ApiBanner";
import { Segmented } from "../components/Segmented";
import { cur, deltaDir, fmt, fmtDelta, fmtMoneyFull, fmtPct } from "../lib/format";
import { cn } from "@/lib/utils";
import type { EsteiraListResponse } from "../types/reports";

const SOURCE_COLORS = {
    flow: "#3b82f6",
    broadcast: "#f59e0b",
    esteira: "#8b5cf6",
};

export function EsteiraView() {
    const setView = useDashboardStore((s) => s.setView);
    const esteiraCompare = useDashboardStore((s) => s.toggles.esteiraCompare);
    const setToggle = useDashboardStore((s) => s.setToggle);
    const { data, error, isLoading, refetch } = useEsteiraList();
    const loading = isLoading && !data;

    const hero = (
        <PageHeader
            icon={Radio}
            title="Esteira"
            subtitle="Receita por esteira, broadcast, página e horário"
        />
    );

    if (error && !data) {
        return (
            <PageShell hero={hero}>
                <ApiBanner error={error} onRetry={() => refetch()} />
            </PageShell>
        );
    }

    const k = data?.kpis;
    const items = data?.items ?? [];
    const cmp = data?.comparison;
    const heatmap = data?.heatmap ?? [];

    const heatmapRevenueSum = heatmap.reduce(
        (a, c) => a + (c.revenue || 0),
        0
    );
    const kpiRevenue = cur(k?.revenue);

    return (
        <PageShell hero={hero}>
            {/* KPI strip */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <KpiCard
                    icon={DollarSign}
                    iconColor="text-emerald-500"
                    label="Receita Esteira"
                    value={
                        loading ? (
                            <SkelText />
                        ) : (
                            "$" +
                            Number(kpiRevenue).toLocaleString("pt-BR", {
                                maximumFractionDigits: 2,
                            })
                        )
                    }
                    delta={fmtDelta(k?.revenue)}
                    deltaDir={deltaDir(k?.revenue)}
                />
                <KpiCard
                    icon={Send}
                    iconColor="text-blue-500"
                    label="Broadcasts"
                    value={
                        loading ? (
                            <SkelText />
                        ) : (
                            fmt(Math.round(cur(k?.broadcasts)))
                        )
                    }
                    sub="no período"
                />
                <KpiCard
                    icon={Users}
                    iconColor="text-violet-500"
                    label="Leads alcançados"
                    value={
                        loading ? (
                            <SkelText />
                        ) : (
                            fmt(Math.round(cur(k?.leadsReached)))
                        )
                    }
                    delta={fmtDelta(k?.leadsReached)}
                    deltaDir={deltaDir(k?.leadsReached)}
                />
                <KpiCard
                    icon={TrendingUp}
                    iconColor="text-amber-500"
                    label="RPM médio"
                    value={
                        loading ? (
                            <SkelText />
                        ) : (
                            "$" + Number(cur(k?.avgRpm)).toFixed(2)
                        )
                    }
                    delta={fmtDelta(k?.avgRpm)}
                    deltaDir={deltaDir(k?.avgRpm)}
                />
                <KpiCard
                    icon={Zap}
                    iconColor="text-rose-500"
                    label="Esteiras ativas"
                    value={loading ? <SkelText /> : fmt(k?.activeEsteiras ?? 0)}
                    sub={
                        k?.pausedEsteiras
                            ? `${k.pausedEsteiras} pausadas`
                            : undefined
                    }
                />
            </div>

            {/* Compare toggle */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-base font-semibold text-gray-800">
                        {esteiraCompare
                            ? "Comparativo · Flow × Broadcast × Esteira"
                            : "Performance da Esteira"}
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                        {esteiraCompare
                            ? "Performance lado a lado das 3 origens"
                            : "Receita, heatmap e rankings"}
                    </p>
                </div>
                <Segmented<"single" | "compare">
                    value={esteiraCompare ? "compare" : "single"}
                    onChange={(v) =>
                        setToggle("esteiraCompare", v === "compare")
                    }
                    options={[
                        { v: "single", l: "Só esteira" },
                        { v: "compare", l: "Comparar tudo" },
                    ]}
                />
            </div>

            {/* Compare card */}
            {cmp && <CompareUnified cmp={cmp} compareAll={esteiraCompare} />}

            {/* Heatmap */}
            <SectionCard
                icon={Clock}
                title="Heatmap · hora × dia · receita esteira"
                subtitle="clique numa célula pra ver o detalhe"
            >
                {loading ? (
                    <DashSkeleton h={240} />
                ) : heatmap.length === 0 ? (
                    <EmptyMsg />
                ) : (
                    <>
                        {Math.abs(heatmapRevenueSum - kpiRevenue) >
                            Math.max(1, kpiRevenue * 0.02) &&
                            heatmapRevenueSum > 0 &&
                            kpiRevenue > 0 && (
                                <div className="mb-3 bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-900 flex items-start gap-2">
                                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>
                                        Total do heatmap (
                                        <strong>
                                            $
                                            {heatmapRevenueSum.toLocaleString(
                                                "pt-BR",
                                                {
                                                    maximumFractionDigits: 2,
                                                }
                                            )}
                                        </strong>
                                        ) diverge do KPI de receita (
                                        <strong>
                                            $
                                            {kpiRevenue.toLocaleString("pt-BR", {
                                                maximumFractionDigits: 2,
                                            })}
                                        </strong>
                                        ). Backend retorna heatmap não escopado
                                        em alguns períodos.
                                    </span>
                                </div>
                            )}
                        <EsteiraHeatmap cells={heatmap} />
                    </>
                )}
            </SectionCard>

            {/* Esteira list */}
            <SectionCard
                icon={Radio}
                title="Esteiras (grupos)"
                subtitle="clique pra abrir o relatório da esteira"
            >
                {loading ? (
                    <DashSkeleton h={300} />
                ) : items.length === 0 ? (
                    <EmptyMsg />
                ) : (
                    <div className="overflow-x-auto -mx-5">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider border-b border-gray-200">
                                    <th className="text-left px-5 py-2">#</th>
                                    <th className="text-left px-5 py-2">
                                        Esteira
                                    </th>
                                    <th className="text-left px-5 py-2">País</th>
                                    <th className="text-left px-5 py-2">
                                        Status
                                    </th>
                                    <th className="text-right px-5 py-2">
                                        Broadcasts
                                    </th>
                                    <th className="text-right px-5 py-2">
                                        Enviadas
                                    </th>
                                    <th className="text-right px-5 py-2">CTR</th>
                                    <th className="text-right px-5 py-2">
                                        Receita
                                    </th>
                                    <th className="text-right px-5 py-2">
                                        vs ant.
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {items.map((e, i) => {
                                    const ctr =
                                        e.ctr != null && e.ctr > 0
                                            ? Math.min(100, e.ctr)
                                            : null;
                                    const deltaPct = e.deltaPct ?? 0;
                                    return (
                                        <tr
                                            key={e.id}
                                            className="cursor-pointer hover:bg-gray-50"
                                            onClick={() =>
                                                setView(
                                                    {
                                                        name: "esteira_group",
                                                        id: e.id,
                                                    },
                                                    e.name
                                                )
                                            }
                                        >
                                            <td className="px-5 py-3 text-xs font-mono text-gray-400">
                                                {String(i + 1).padStart(2, "0")}
                                            </td>
                                            <td
                                                className="px-5 py-3 font-medium text-gray-800 truncate"
                                                title={e.name}
                                            >
                                                {e.name}
                                            </td>
                                            <td className="px-5 py-3">
                                                <span className="text-xs text-gray-500">
                                                    {e.countryCode || "—"}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3">
                                                {e.status === "active" ? (
                                                    <BadgeHi color="emerald">
                                                        Ativo
                                                    </BadgeHi>
                                                ) : (
                                                    <BadgeHi color="amber">
                                                        Pausado
                                                    </BadgeHi>
                                                )}
                                            </td>
                                            <td className="px-5 py-3 text-right font-mono text-xs">
                                                {fmt(e.broadcasts ?? 0)}
                                            </td>
                                            <td className="px-5 py-3 text-right font-mono text-xs">
                                                {fmt(e.sends ?? 0)}
                                            </td>
                                            <td className="px-5 py-3 text-right">
                                                {ctr != null ? (
                                                    <span
                                                        className={cn(
                                                            "inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full",
                                                            ctr >= 8
                                                                ? "bg-emerald-50 text-emerald-700"
                                                                : ctr >= 7
                                                                  ? "bg-amber-50 text-amber-700"
                                                                  : "bg-gray-100 text-gray-600"
                                                        )}
                                                    >
                                                        {fmtPct(ctr)}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-300">
                                                        —
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-5 py-3 text-right font-semibold text-gray-900 font-mono">
                                                $
                                                {Number(
                                                    e.revenue
                                                ).toLocaleString("pt-BR", {
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td
                                                className={cn(
                                                    "px-5 py-3 text-right font-mono text-xs",
                                                    deltaPct > 0
                                                        ? "text-emerald-600"
                                                        : deltaPct < 0
                                                          ? "text-rose-600"
                                                          : "text-gray-400"
                                                )}
                                            >
                                                {e.deltaPct == null
                                                    ? "—"
                                                    : `${deltaPct > 0 ? "+" : ""}${deltaPct.toFixed(1)}%`}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </SectionCard>
        </PageShell>
    );
}

// Unified comparison — single rounded card with segmented share bar + per-source columns.
// When compareAll=false, shows only the esteira column.
function CompareUnified({
    cmp,
    compareAll,
}: {
    cmp: NonNullable<EsteiraListResponse["comparison"]>;
    compareAll: boolean;
}) {
    type Src = {
        key: "flow" | "broadcast" | "esteira";
        label: string;
        color: string;
        soft: string;
        text: string;
        data:
            | EsteiraListResponse["comparison"]["flow"]
            | EsteiraListResponse["comparison"]["broadcast"]
            | EsteiraListResponse["comparison"]["esteira"];
        countLabel: string;
    };
    // countLabel só aparece quando o backend retorna > 0 — evita o
    // "0 ativos / 0 disparos" zoado quando o endpoint /esteira não
    // popula `activeCount`/`shotCount` (que não são contas críticas, são
    // só decoração da coluna). Se vier 0 mostra `sharePct` em vez.
    const flowCount = cmp.flow?.activeCount ?? 0;
    const broadcastCount = cmp.broadcast?.shotCount ?? 0;
    const esteiraCount = cmp.esteira?.activeCount ?? 0;
    const labelOrShare = (n: number, word: string, sharePct?: number) =>
        n > 0
            ? `${n} ${word}`
            : sharePct != null && sharePct > 0
              ? `${sharePct.toFixed(1)}% da receita`
              : "";
    const all: Src[] = [
        {
            key: "flow",
            label: "Flow",
            color: SOURCE_COLORS.flow,
            soft: "bg-blue-50",
            text: "text-blue-700",
            data: cmp.flow,
            countLabel: labelOrShare(flowCount, "ativos", cmp.flow?.sharePct),
        },
        {
            key: "broadcast",
            label: "Broadcast",
            color: SOURCE_COLORS.broadcast,
            soft: "bg-amber-50",
            text: "text-amber-700",
            data: cmp.broadcast,
            countLabel: labelOrShare(
                broadcastCount,
                "disparos",
                cmp.broadcast?.sharePct
            ),
        },
        {
            key: "esteira",
            label: "Esteira",
            color: SOURCE_COLORS.esteira,
            soft: "bg-violet-50",
            text: "text-violet-700",
            data: cmp.esteira,
            countLabel: labelOrShare(
                esteiraCount,
                "esteiras",
                cmp.esteira?.sharePct
            ),
        },
    ];

    const sources: Src[] = compareAll ? all : all.filter((s) => s.key === "esteira");

    const totalAll = all.reduce((s, x) => s + (x.data?.revenue || 0), 0);
    const total = compareAll
        ? totalAll
        : sources.reduce((s, x) => s + (x.data?.revenue || 0), 0);
    const money = (v: number | undefined | null) =>
        "$" +
        Number(v ?? 0).toLocaleString("pt-BR", {
            maximumFractionDigits: 2,
        });
    const top = [...all].sort(
        (a, b) => (b.data?.revenue || 0) - (a.data?.revenue || 0)
    )[0];
    const topPct =
        totalAll > 0
            ? ((top.data?.revenue || 0) / totalAll) * 100
            : 0;

    return (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="px-6 pt-5 pb-4 flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
                        {compareAll
                            ? "Receita total · período"
                            : "Receita Esteira · período"}
                    </p>
                    <p className="text-3xl font-bold text-gray-900 mt-0.5 font-mono tabular-nums">
                        {money(total)}
                    </p>
                </div>
                {compareAll && (
                    <div className="flex items-center gap-2 text-xs">
                        <span className="text-gray-400">Maior origem:</span>
                        <span
                            className={cn(
                                "inline-flex items-center gap-1.5 font-semibold px-2.5 py-1 rounded-full",
                                top.soft,
                                top.text
                            )}
                        >
                            <span
                                className="w-2 h-2 rounded-full"
                                style={{ background: top.color }}
                            />
                            {top.label} · {topPct.toFixed(0)}%
                        </span>
                    </div>
                )}
            </div>

            {/* Segmented share bar (only when comparing all) */}
            {compareAll && (
                <div className="px-6">
                    <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
                        {all.map((s) => {
                            const pct =
                                totalAll > 0
                                    ? ((s.data?.revenue || 0) / totalAll) * 100
                                    : 0;
                            if (pct <= 0) return null;
                            return (
                                <div
                                    key={s.key}
                                    style={{
                                        width: `${pct}%`,
                                        background: s.color,
                                    }}
                                    title={`${s.label}: ${pct.toFixed(1)}%`}
                                />
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Per-source rows */}
            <div
                className={cn(
                    "grid gap-px bg-gray-100 mx-6 mb-6 rounded-xl overflow-hidden border border-gray-100",
                    compareAll
                        ? "grid-cols-1 sm:grid-cols-3 mt-5"
                        : "grid-cols-1 mt-5"
                )}
            >
                {sources.map((s) => {
                    const d = s.data || ({} as Src["data"]);
                    const pct =
                        totalAll > 0
                            ? ((d.revenue || 0) / totalAll) * 100
                            : 0;
                    const stats: Array<[string, string]> = [];
                    if ((d.rpm ?? 0) > 0)
                        stats.push(["RPM", "$" + (d.rpm as number).toFixed(2)]);
                    if ((d.ctr ?? 0) > 0)
                        stats.push(["CTR", (d.ctr as number).toFixed(1) + "%"]);
                    if ((d.conv ?? 0) > 0)
                        stats.push([
                            "Conv.",
                            (d.conv as number).toFixed(1) + "%",
                        ]);
                    return (
                        <div key={s.key} className="bg-white p-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                                    <span
                                        className="w-2.5 h-2.5 rounded-sm"
                                        style={{ background: s.color }}
                                    />
                                    {s.label}
                                </span>
                                {compareAll && (
                                    <span className="text-[10px] font-mono text-gray-400">
                                        {pct.toFixed(0)}%
                                    </span>
                                )}
                            </div>
                            <p className="text-xl font-bold text-gray-900 font-mono tabular-nums">
                                {money(d.revenue)}
                            </p>
                            {s.countLabel && (
                                <p className="text-[11px] text-gray-400 mt-0.5">
                                    {s.countLabel}
                                </p>
                            )}
                            {stats.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-x-4 gap-y-1.5">
                                    {stats.map(([k, v]) => (
                                        <div
                                            key={k}
                                            className="flex items-baseline gap-1"
                                        >
                                            <span className="text-[9px] uppercase text-gray-400 tracking-wider">
                                                {k}
                                            </span>
                                            <span className="text-xs font-semibold text-gray-800 font-mono tabular-nums">
                                                {v}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ── Heatmap day × hour from { weekday: 0=Sun..6=Sat, hour: 0..23, revenue, clicks }
// Visual layout: 7 rows in seg→ter→qua→qui→sex→sáb→dom order, 24 hour columns.
type HeatCell = { weekday: number; hour: number; clicks: number; revenue: number };
type Focus = { r: number; c: number } | null;

const ROW_TO_WEEKDAY = [1, 2, 3, 4, 5, 6, 0]; // row 0 = seg (weekday 1), row 6 = dom (weekday 0)
const DAY_SHORT = ["seg", "ter", "qua", "qui", "sex", "sáb", "dom"];
const DAY_LONG = [
    "Segunda",
    "Terça",
    "Quarta",
    "Quinta",
    "Sexta",
    "Sábado",
    "Domingo",
];

function EsteiraHeatmap({ cells }: { cells: HeatCell[] }) {
    const [hover, setHover] = useState<Focus>(null);
    const [selected, setSelected] = useState<Focus>(null);

    const cellMap = useMemo(() => {
        const m = new Map<string, HeatCell>();
        cells.forEach((c) => m.set(`${c.weekday}-${c.hour}`, c));
        return m;
    }, [cells]);

    const max = useMemo(
        () => Math.max(...cells.map((c) => c.revenue || 0), 0),
        [cells]
    );

    if (max <= 0) {
        return (
            <div className="bg-slate-50 rounded-lg py-8 text-center text-gray-400 border border-gray-100">
                <Clock className="w-6 h-6 mx-auto mb-2" />
                <p className="text-sm">Sem atividade no período</p>
            </div>
        );
    }

    const activeWeekdays = new Set(
        cells.filter((c) => (c.revenue || 0) > 0).map((c) => c.weekday)
    ).size;
    const shortRangeHint = activeWeekdays <= 1;

    const focus = selected || hover;
    const focusWeekday = focus ? ROW_TO_WEEKDAY[focus.r] : null;
    const focusCell =
        focus && focusWeekday != null
            ? cellMap.get(`${focusWeekday}-${focus.c}`)
            : null;

    const rowTotal =
        focus && focusWeekday != null
            ? cells
                  .filter((c) => c.weekday === focusWeekday)
                  .reduce((s, c) => s + (c.revenue || 0), 0)
            : 0;
    const colTotal = focus
        ? cells
              .filter((c) => c.hour === focus.c)
              .reduce((s, c) => s + (c.revenue || 0), 0)
        : 0;
    const grandTotal = cells.reduce((s, c) => s + (c.revenue || 0), 0);

    return (
        <div>
            {shortRangeHint && (
                <div className="mb-3 bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-900 flex items-start gap-2">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                        O período filtrado cai em{" "}
                        <strong>um único dia da semana</strong>, então só essa
                        linha tem dados. O mapa fica mais útil com{" "}
                        <strong>30 ou 90 dias</strong> (revela o padrão
                        semanal).
                    </span>
                </div>
            )}
            <div
                className="grid gap-1"
                style={{ gridTemplateColumns: "44px repeat(24, 1fr)" }}
            >
                <div />
                {Array.from({ length: 24 }, (_, i) => (
                    <div
                        key={i}
                        className={cn(
                            "text-[10px] text-center font-mono transition-colors",
                            focus?.c === i
                                ? "text-violet-700 font-bold"
                                : "text-gray-400"
                        )}
                    >
                        {i % 2 === 0 ? String(i).padStart(2, "0") : ""}
                    </div>
                ))}
                {Array.from({ length: 7 }, (_, r) => {
                    const weekday = ROW_TO_WEEKDAY[r];
                    return (
                        <div key={`row-${r}`} className="contents">
                            <div
                                className={cn(
                                    "text-xs font-medium flex items-center transition-colors",
                                    focus?.r === r
                                        ? "text-violet-700 font-bold"
                                        : "text-gray-500"
                                )}
                            >
                                {DAY_SHORT[r]}
                            </div>
                            {Array.from({ length: 24 }, (_, c) => {
                                const cell = cellMap.get(`${weekday}-${c}`);
                                const v = cell?.revenue || 0;
                                const clicks = cell?.clicks || 0;
                                const a = v / max;
                                const isHot = a > 0.7;
                                const bg =
                                    v === 0
                                        ? "rgba(241,245,249,0.7)"
                                        : isHot
                                          ? `rgba(245, 158, 11, ${a})`
                                          : `rgba(139, 92, 246, ${Math.max(a * 0.7, 0.18)})`;
                                const isSelected =
                                    selected?.r === r && selected?.c === c;
                                const isHovered =
                                    hover?.r === r && hover?.c === c;
                                const isFocused = isSelected || isHovered;
                                return (
                                    <button
                                        key={`cell-${r}-${c}`}
                                        type="button"
                                        onMouseEnter={() =>
                                            setHover({ r, c })
                                        }
                                        onMouseLeave={() => setHover(null)}
                                        onClick={() =>
                                            setSelected(
                                                isSelected ? null : { r, c }
                                            )
                                        }
                                        className="rounded-sm transition-all relative"
                                        style={{
                                            height: 30,
                                            background: bg,
                                            border: isFocused
                                                ? "1.5px solid #6d28d9"
                                                : "1px solid rgba(255,255,255,0.6)",
                                            transform: isFocused
                                                ? "scale(1.18)"
                                                : "scale(1)",
                                            zIndex: isFocused ? 5 : 1,
                                            boxShadow: isSelected
                                                ? "0 4px 12px rgba(139,92,246,0.4)"
                                                : "none",
                                        }}
                                        title={`${DAY_SHORT[r]} ${c}h · ${fmtMoneyFull(v)}${clicks ? ` · ${fmt(clicks)} cliques` : ""}`}
                                    />
                                );
                            })}
                        </div>
                    );
                })}
            </div>

            {focus && (
                <div className="mt-4 bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <p className="text-[10px] uppercase font-semibold tracking-wider text-violet-700 mb-1">
                            {selected
                                ? "Célula selecionada"
                                : "Passando o mouse"}
                        </p>
                        <p className="text-lg font-bold text-gray-900 leading-tight">
                            {DAY_LONG[focus.r]}
                        </p>
                        <p className="text-sm font-mono text-gray-700 tabular-nums">
                            {String(focus.c).padStart(2, "0")}:00 –{" "}
                            {String(focus.c).padStart(2, "0")}:59
                        </p>
                    </div>
                    <div>
                        <p className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 mb-1">
                            Receita
                        </p>
                        <p className="text-2xl font-bold text-emerald-600 font-mono tabular-nums">
                            $
                            {(focusCell?.revenue || 0).toLocaleString("pt-BR", {
                                maximumFractionDigits: 2,
                            })}
                        </p>
                        {focusCell?.clicks != null && (
                            <p className="text-[11px] text-gray-500 mt-0.5 font-mono tabular-nums">
                                {fmt(focusCell.clicks)} cliques
                            </p>
                        )}
                    </div>
                    <div>
                        <p className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 mb-1">
                            % da hora ({focus.c}h)
                        </p>
                        <p className="text-xl font-bold text-gray-900 font-mono tabular-nums">
                            {colTotal > 0
                                ? (
                                      ((focusCell?.revenue || 0) / colTotal) *
                                      100
                                  ).toFixed(1)
                                : "0"}
                            %
                        </p>
                        <p className="text-[11px] text-gray-500 mt-0.5 font-mono tabular-nums">
                            total {focus.c}h: $
                            {colTotal.toFixed(2)}
                        </p>
                    </div>
                    <div>
                        <p className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 mb-1">
                            % do dia ({DAY_SHORT[focus.r]})
                        </p>
                        <p className="text-xl font-bold text-gray-900 font-mono tabular-nums">
                            {rowTotal > 0
                                ? (
                                      ((focusCell?.revenue || 0) / rowTotal) *
                                      100
                                  ).toFixed(1)
                                : "0"}
                            %
                        </p>
                        <p className="text-[11px] text-gray-500 mt-0.5 font-mono tabular-nums">
                            total {DAY_SHORT[focus.r]}: $
                            {rowTotal.toFixed(2)}
                        </p>
                    </div>
                </div>
            )}

            <div className="mt-3 flex items-center justify-between text-xs text-gray-500 flex-wrap gap-2">
                <span>
                    linha = dia · coluna = hora · clique numa célula pra fixar ·
                    total ${grandTotal.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}
                </span>
                <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-sm bg-violet-200" />
                        baixo
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-sm bg-violet-500" />
                        médio
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-sm bg-amber-500" />
                        pico
                    </span>
                    {selected && (
                        <button
                            type="button"
                            onClick={() => setSelected(null)}
                            className="text-violet-700 font-medium hover:underline"
                        >
                            limpar seleção
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function EmptyMsg() {
    return (
        <div className="text-center py-8">
            <Layers className="w-6 h-6 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Sem dados no período</p>
        </div>
    );
}
